import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatINR } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Users, RefreshCw, Plus, Send, Loader2, Sparkles, ExternalLink, AlertTriangle, MousePointerClick, MailOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const MailchimpTab = () => {
    const [ov, setOv] = useState(null);
    const [camps, setCamps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/mailchimp/overview");
            setOv(data);
            if (data.configured) {
                const c = await api.get("/admin/mailchimp/campaigns?limit=10");
                setCamps(c.data.campaigns || []);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load Mailchimp data");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const sync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/mailchimp/sync-customers");
            toast.success(`Synced ${data.total} customers · ${data.added} new, ${data.updated} updated, ${data.skipped} skipped`);
            if (data.problems?.length) console.warn("Mailchimp sync problems", data.problems);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    const send = async (c) => {
        if (!window.confirm(`Send "${c.subject_line || c.title}" to all ${ov.subscribers} subscribers now? This cannot be undone.`)) return;
        try {
            await api.post(`/admin/mailchimp/campaigns/${c.id}/send`);
            toast.success("Campaign is sending");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete the draft "${c.title}"?`)) return;
        try {
            await api.delete(`/admin/mailchimp/campaigns/${c.id}`);
            toast.success("Draft deleted");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete");
        }
    };

    if (loading) return <div className="text-sm text-stone-500 py-10" data-testid="mailchimp-loading">Loading Mailchimp…</div>;

    if (!ov?.configured) {
        return (
            <div className="bg-amber-50 border border-amber-300 p-6" data-testid="mailchimp-not-configured">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <h2 className="font-serif text-xl text-stone-900">Mailchimp isn't connected yet</h2>
                        <p className="text-sm text-stone-700 mt-2">{ov?.reason}</p>
                        <div className="text-xs text-stone-600 mt-3 space-y-1">
                            <div><strong>MAILCHIMP_API_KEY</strong> — Mailchimp → Account &amp; billing → Extras → API keys. Must end in a datacenter suffix like <span className="font-mono">-us21</span>.</div>
                            <div><strong>MAILCHIMP_AUDIENCE_ID</strong> — Audience → Settings → Audience name and defaults.</div>
                        </div>
                        <div className="mt-4 flex gap-6 text-sm">
                            <div><span className="text-stone-500">Customers ready to sync:</span> <strong>{ov?.local_customers ?? 0}</strong></div>
                            <div><span className="text-stone-500">Captured locally:</span> <strong>{ov?.synced_locally ?? 0}</strong></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="mailchimp-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-serif text-xl text-stone-900">
                        Mailchimp · {ov.audience_name}
                        <span className="ml-2 text-[10px] uppercase tracking-widest border border-emerald-500 text-emerald-700 px-1.5 py-0.5">Connected</span>
                    </h2>
                    <p className="text-xs text-stone-500 mt-1 font-mono">Audience {ov.audience_id} · {ov.datacenter}</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={sync} disabled={syncing} variant="outline" className="rounded-none border-stone-400" data-testid="mailchimp-sync-btn">
                        {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Sync {ov.syncable_customers ?? ov.local_customers} customers
                    </Button>
                    <Button onClick={() => setOpen(true)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="mailchimp-new-campaign-btn">
                        <Plus className="w-4 h-4 mr-2" />New campaign
                    </Button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat icon={Users} label="Subscribers" value={ov.subscribers} sub={ov.pending ? `+${ov.pending} awaiting confirmation` : null} accent data-testid="mc-subscribers" />
                <Stat icon={Users} label="Total contacts" value={ov.total_contacts} sub={`${ov.unsubscribes} unsubscribed · ${ov.cleaned} cleaned`} />
                <Stat icon={MailOpen} label="Avg open rate" value={`${ov.avg_open_rate}%`} />
                <Stat icon={MousePointerClick} label="Avg click rate" value={`${ov.avg_click_rate}%`} />
            </div>

            {ov.local_customers > (ov.syncable_customers ?? ov.local_customers) && (
                <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900" data-testid="mc-undeliverable-note">
                    {ov.local_customers - ov.syncable_customers} of your {ov.local_customers} customer records use non-deliverable
                    test domains (example.com and similar) and are skipped by the sync — pushing them to Mailchimp would only
                    bounce and damage your sender reputation.
                </div>
            )}

            {ov.last_sync && (
                <div className="text-xs text-stone-500" data-testid="mc-last-sync">
                    Last sync {new Date(ov.last_sync.at).toLocaleString()} by {ov.last_sync.by} — {ov.last_sync.added} added, {ov.last_sync.updated} updated, {ov.last_sync.skipped} skipped.
                </div>
            )}

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <div className="px-4 py-3 border-b border-stone-100 text-xs uppercase tracking-widest text-stone-500">Recent campaigns</div>
                {camps.length === 0 ? (
                    <div className="p-6 text-sm text-stone-500" data-testid="mc-no-campaigns">No campaigns yet. Create one to get started.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="mc-campaigns-table">
                        <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                            <tr>
                                <th className="p-3">Campaign</th>
                                <th className="p-3">Status</th>
                                <th className="p-3 text-right">Sent</th>
                                <th className="p-3 text-right">Opens</th>
                                <th className="p-3 text-right">Clicks</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {camps.map((c) => (
                                <tr key={c.id} className="border-t border-stone-100" data-testid={`mc-campaign-${c.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{c.title}</div>
                                        <div className="text-xs text-stone-500">{c.subject_line}</div>
                                    </td>
                                    <td className="p-3">
                                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${c.status === "sent" ? "border-emerald-500 text-emerald-700" : c.status === "save" ? "border-stone-400 text-stone-600" : "border-sky-500 text-sky-700"}`}>
                                            {c.status === "save" ? "draft" : c.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right font-mono">{c.emails_sent || "—"}</td>
                                    <td className="p-3 text-right font-mono">{c.open_rate == null ? "—" : `${c.open_rate}%`}</td>
                                    <td className="p-3 text-right font-mono">{c.click_rate == null ? "—" : `${c.click_rate}%`}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        {c.status === "save" && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => send(c)} className="rounded-none border-sky-500 text-sky-700 mr-2" data-testid={`mc-send-${c.id}`}>
                                                    <Send className="w-3.5 h-3.5 mr-1" />Send now
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => remove(c)} className="rounded-none text-red-600" data-testid={`mc-delete-${c.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </>
                                        )}
                                        {c.archive_url && (
                                            <a href={c.archive_url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1">
                                                View <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <AbandonedCarts />

            <CampaignDialog open={open} onOpenChange={setOpen} onCreated={load} />
        </div>
    );
};

const AbandonedCarts = () => {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/carts/abandoned?limit=25");
            setRows(data);
        } catch { /* non-critical panel */ }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/carts/run-abandonment");
            toast.success(
                data.skipped ? "Abandoned-cart reminders are switched off in Settings"
                    : `${data.sent_first} first reminder(s), ${data.sent_followup} follow-up(s) sent`,
            );
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not run the sweep");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-stone-200" data-testid="abandoned-carts-panel">
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-stone-500">Open &amp; abandoned carts</div>
                <Button size="sm" variant="outline" onClick={runNow} disabled={busy} className="rounded-none border-stone-400" data-testid="run-abandonment-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Run reminder sweep
                </Button>
            </div>
            {rows.length === 0 ? (
                <div className="p-6 text-sm text-stone-500" data-testid="no-abandoned-carts">
                    No open carts right now. Signed-in shoppers' carts appear here, and reminders go out automatically at 2 hours and again at 24 hours.
                </div>
            ) : (
                <table className="w-full text-sm" data-testid="abandoned-carts-table">
                    <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                        <tr>
                            <th className="p-3">Shopper</th>
                            <th className="p-3 text-right">Items</th>
                            <th className="p-3 text-right">Value</th>
                            <th className="p-3">Last activity</th>
                            <th className="p-3">Reminders</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-stone-100" data-testid={`abandoned-cart-${r.id}`}>
                                <td className="p-3">
                                    <div className="text-stone-900">{r.name || "—"}</div>
                                    <div className="text-xs text-stone-500">{r.email}</div>
                                </td>
                                <td className="p-3 text-right font-mono">{r.items}</td>
                                <td className="p-3 text-right font-mono">{formatINR(r.total)}</td>
                                <td className="p-3 text-xs text-stone-500">{new Date(r.updated_at).toLocaleString()}</td>
                                <td className="p-3 text-xs">
                                    {r.reminder_sent_at ? <span className="text-emerald-700">1st sent</span> : <span className="text-stone-400">pending</span>}
                                    {r.reminder2_sent_at && <span className="text-emerald-700"> · 2nd sent</span>}
                                    {r.recovered_at && <span className="text-sky-700"> · link opened</span>}
                                </td>
                                <td className="p-3 text-right">
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(r.recovery_link); toast.success("Recovery link copied"); }}
                                        className="text-xs text-sky-700 hover:underline"
                                        data-testid={`copy-recovery-${r.id}`}
                                    >
                                        Copy link
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const Stat = ({ icon: Icon, label, value, sub, accent = false, ...rest }) => (
    <div className={`p-4 border ${accent ? "bg-sky-50 border-sky-200" : "bg-white border-stone-200"}`} {...rest}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
            <Icon className="w-3.5 h-3.5" />{label}
        </div>
        <div className={`font-serif text-3xl mt-1 ${accent ? "text-sky-800" : "text-stone-900"}`}>{value}</div>
        {sub && <div className="text-[11px] text-stone-500 mt-1">{sub}</div>}
    </div>
);

const CampaignDialog = ({ open, onOpenChange, onCreated }) => {
    const [f, setF] = useState({ title: "", subject_line: "", preview_text: "", from_name: "", reply_to: "", html: "", plain_text: "" });
    const [goal, setGoal] = useState("");
    const [tone, setTone] = useState("conversational");
    const [drafting, setDrafting] = useState(false);
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const draft = async () => {
        if (goal.trim().length < 3) return toast.error("Tell the AI what this email is for");
        setDrafting(true);
        try {
            const { data } = await api.post("/admin/mailchimp/ai-draft", { goal, tone });
            setF({
                title: data.title, subject_line: data.subject_line, preview_text: data.preview_text,
                from_name: data.from_name, reply_to: data.reply_to, html: data.html, plain_text: data.plain_text,
            });
            toast.success("Draft written — review and edit before saving");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI draft failed");
        } finally {
            setDrafting(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/admin/mailchimp/campaigns", f);
            toast.success("Draft created in Mailchimp");
            window.open(data.edit_url, "_blank", "noreferrer");
            onOpenChange(false);
            await onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create the campaign");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mc-campaign-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl">New Mailchimp campaign</DialogTitle></DialogHeader>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-4 space-y-2">
                    <div className="text-sm font-medium text-stone-800 inline-flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-700" />Let AI write it
                    </div>
                    <Textarea
                        value={goal}
                        onChange={(e) => setGoal(e.target.value)}
                        placeholder="e.g. Monsoon immunity guide with a 10% coupon on our children's range"
                        className="rounded-none bg-white min-h-[60px]"
                        data-testid="mc-ai-goal"
                    />
                    <div className="flex gap-2">
                        <Select value={tone} onValueChange={setTone}>
                            <SelectTrigger className="rounded-none bg-white w-44" data-testid="mc-ai-tone"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="conversational">Conversational</SelectItem>
                                <SelectItem value="educational">Educational</SelectItem>
                                <SelectItem value="festive">Festive</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button onClick={draft} disabled={drafting} className="rounded-none bg-purple-700 hover:bg-purple-800 text-white" data-testid="mc-ai-draft-btn">
                            {drafting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            {drafting ? "Writing…" : "Write draft"}
                        </Button>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Campaign name (internal)" v={f.title} on={set("title")} t="mc-title" />
                    <Field label="Subject line" v={f.subject_line} on={set("subject_line")} t="mc-subject" />
                    <Field label="Preview text" v={f.preview_text} on={set("preview_text")} t="mc-preview" />
                    <Field label="From name" v={f.from_name} on={set("from_name")} t="mc-from" />
                    <Field label="Reply-to email" v={f.reply_to} on={set("reply_to")} t="mc-replyto" />
                </div>
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">Email HTML</div>
                    <Textarea value={f.html} onChange={set("html")} className="rounded-none font-mono text-xs min-h-[200px]" data-testid="mc-html" />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="mc-save-campaign">
                        {saving ? "Creating…" : "Create draft in Mailchimp"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, v, on, t }) => (
    <div>
        <div className="label-eyebrow text-stone-500 mb-1">{label}</div>
        <Input value={v} onChange={on} className="rounded-none" data-testid={t} />
    </div>
);
